import { useEffect, useMemo, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { resourceScopeTag, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  PRICE_TYPES_PAGE_SIZE,
  ProductCardScreen,
  cardFields,
  draftDiscarded,
  draftFieldChanged,
  draftFieldTakenFromServer,
  draftRebased,
  makeSelectProductCardVm,
  makeSelectProductFactsVm,
  makeSelectProductPhotosVm,
  onecPriceList,
  onecProductBarcodes,
  onecProductCard,
  onecProductPhotos,
  onecProductPrices,
  onecProductStock,
  operationDismissed,
  photoMainRequested,
  photoOperationCancelRequested,
  photoOperationDismissed,
  photoOperationResendRequested,
  photoSelected,
  photosReloadRequested,
  photoUploadRequested,
  pricingApi,
  productBarcodeRequested,
  productMarkDeletedRequested,
  productOperationResendRequested,
  productReloadRequested,
  productSaveRequested,
  stashPhotoFile,
  type OneCProductArgs,
  type OneCProductPhotosArgs,
  type PriceListArgs,
  type ProductCardActions,
  type ProductPhotosActions,
  normalizeRid,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/**
 * Binding карточки товара 1С: rid и ключ — из окна. Черновик, операции и
 * запросы — по паре (rid, ключ): тот же товар другой базы — другое окно и
 * другой черновик. Фото, цены и остатки живут в самой карточке (отдельных
 * окон «Фото» и «Цены товара» нет): цены и остатки только читаются, закупок
 * здесь нет — они идут через поставки.
 */
export function OneCProductCardBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  // rid — в одном написании: ключи кэша, теги и черновики не расходятся по регистру GUID.
  const resourceId = normalizeRid(window.params.rid ?? '')
  const refKey = window.params.key ?? ''
  const args = useMemo<OneCProductArgs>(() => ({ resourceId, refKey }), [resourceId, refKey])
  const skip = resourceId === '' || refKey === ''
  onecProductCard.useQuerySubscription(skip ? skipToken : args)
  onecProductBarcodes.useQuerySubscription(skip ? skipToken : args)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectProductCardVm()
    const selectCard = onecProductCard.select(args)
    const selectBarcodes = onecProductBarcodes.select(args)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        refKey,
        card: selectCard(state),
        barcodes: selectBarcodes(state),
        intent: state.onec.products,
        operations: state.onec.productOperations,
      })
  }, [args, refKey, resourceId])
  const vm = useAppSelector(selectVm)

  const pricingArgs = skip ? null : args
  const priceTypesArgs = useMemo<PriceListArgs | null>(
    () => (skip ? null : { resourceId, priceType: null, query: '', page: 0, pageSize: PRICE_TYPES_PAGE_SIZE }),
    [resourceId, skip],
  )
  const stock = useQueryResult(onecProductStock, pricingArgs)
  const prices = useQueryResult(onecProductPrices, pricingArgs)
  const priceTypes = useQueryResult(onecPriceList, priceTypesArgs)
  const selectFacts = useMemo(() => makeSelectProductFactsVm(), [])
  const facts = selectFacts({ stock, prices, priceTypes })

  // Фото товара: те же запросы и команды, что раньше были в отдельном окне «Фото».
  const photoArgs = useMemo<OneCProductPhotosArgs>(() => ({ resourceId, refKey }), [resourceId, refKey])
  onecProductPhotos.useQuerySubscription(skip ? skipToken : photoArgs)
  const selectPhotosVm = useMemo(() => {
    const selectPackageVm = makeSelectProductPhotosVm()
    const selectPhotos = onecProductPhotos.select(photoArgs)
    return (state: RootState) =>
      selectPackageVm({ resourceId, refKey, photos: selectPhotos(state), intent: state.onec.photos })
  }, [photoArgs, refKey, resourceId])
  const photosVm = useAppSelector(selectPhotosVm)
  const photoVersion = useRef(photosVm.configurationVersion)
  useEffect(() => {
    photoVersion.current = photosVm.configurationVersion
  }, [photosVm.configurationVersion])

  // Последний vm для обработчиков: actions не пересоздаются на каждый ответ службы.
  const latest = useRef(vm)
  useEffect(() => {
    latest.current = vm
  }, [vm])

  const actions = useMemo<ProductCardActions>(() => {
    const version = () => latest.current.configurationVersion ?? ''
    return {
      fieldChanged: (field, value) => {
        const card = latest.current.card
        if (card === null) return
        dispatch(
          draftFieldChanged({
            resourceId,
            target: refKey,
            field,
            value,
            base: cardFields(card),
            baseVersion: latest.current.configurationVersion,
            now: new Date().toISOString(),
          }),
        )
      },
      save: () => dispatch(productSaveRequested({ resourceId, target: refKey })),
      discard: () => dispatch(draftDiscarded({ resourceId, target: refKey })),
      takeServer: (field) => {
        if (field === 'barcode') return
        const server = latest.current.form[field].server
        if (server !== null) dispatch(draftFieldTakenFromServer({ resourceId, target: refKey, field, server }))
      },
      keepMine: () => {
        const card = latest.current.card
        if (card === null) return
        dispatch(draftRebased({ resourceId, target: refKey, base: cardFields(card), baseVersion: latest.current.configurationVersion }))
      },
      resend: (key) => dispatch(productOperationResendRequested({ key })),
      dismiss: (key) => dispatch(operationDismissed({ key })),
      reload: () => {
        dispatch(productReloadRequested({ resourceId, refKey }))
        dispatch(photosReloadRequested({ resourceId, refKey }))
        dispatch(pricingApi.util.invalidateTags([resourceScopeTag(resourceId)]))
      },
      markForDeletion: () =>
        dispatch(productMarkDeletedRequested({ resourceId, refKey, expectedConfigurationVersion: version() })),
      addBarcode: (barcode) =>
        dispatch(
          productBarcodeRequested({ resourceId, refKey, action: 'barcode-add', barcode, expectedConfigurationVersion: version() }),
        ),
      deleteBarcode: (barcode) =>
        dispatch(
          productBarcodeRequested({ resourceId, refKey, action: 'barcode-delete', barcode, expectedConfigurationVersion: version() }),
        ),
      setMainBarcode: (barcode) =>
        dispatch(
          productBarcodeRequested({ resourceId, refKey, action: 'barcode-main', barcode, expectedConfigurationVersion: version() }),
        ),
      close: window.close,
      // Оболочка окна умеет сторожа закрытия; без неё карточка просто закрывается без вопроса.
      closeGuard: (guard) => window.setCloseGuard?.(guard),
    }
  }, [dispatch, refKey, resourceId, window])

  const photoActions = useMemo<ProductPhotosActions>(
    () => ({
      select: (fileKey) => dispatch(photoSelected({ resourceId, refKey, fileKey })),
      setMain: (fileKey) =>
        dispatch(photoMainRequested({ resourceId, refKey, fileKey, expectedConfigurationVersion: photoVersion.current ?? '' })),
      upload: (file) =>
        dispatch(
          photoUploadRequested({
            resourceId,
            refKey,
            // Байты — в памяти страницы пакета, в действие уходит только ссылка на них.
            fileToken: stashPhotoFile(resourceId, file),
            fileName: file.name,
            expectedConfigurationVersion: photoVersion.current ?? '',
          }),
        ),
      resend: (key) => dispatch(photoOperationResendRequested({ key })),
      cancel: (key) => dispatch(photoOperationCancelRequested({ key })),
      dismiss: (key) => dispatch(photoOperationDismissed({ key })),
      reload: () => dispatch(photosReloadRequested({ resourceId, refKey })),
    }),
    [dispatch, refKey, resourceId],
  )

  return <ProductCardScreen vm={vm} actions={actions} facts={facts} photos={{ vm: photosVm, actions: photoActions }} />
}
